import styled, { useTheme } from 'styled-components';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { ApplePayWidget } from '../../../components/ApplePayWidget';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import ApplePayButton from '../../../components/ApplePayButton';
import Modal from '../../../components/Modal';
import { Heading3Text, ParagraphLargeText } from '../../../components/Texts';
import { formatUSD } from '../../../common/currency';
import UsdcIcon from '../../../assets/icons/usdc.svg?react';
import LockIcon from '../../../assets/icons/lock.svg?react';
import { useSolana } from '../../../hooks/useSolana';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';
import useApplePayCheckout from '../../../hooks/useApplePayCheckout';
import { useGetCurrentUserProfileQuery } from '../../../redux/api/authorized/auth';

const ContentContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  width: 100%;
  max-width: 1279px;
  box-sizing: border-box;
  text-align: left;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 32px;
  }
`;

const HighCohesionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Line = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.border.secondary};
  width: 100%;
`;

const ApplePayModal = ({ onClose, amount, onSuccessCallback }) => {
  const theme = useTheme();
  const [toastAlertShown, setToastAlertShown] = useState(false);
  const [toastAlert, setToastAlert] = useState(null);
  const { refreshTokenBalances } = useSolana();
  const dispatch = useDispatch();
  const { data: currentUser } = useGetCurrentUserProfileQuery();
  const isWeb = !Capacitor.isNativePlatform();
  const autoStartedRef = useRef(false);
  // Only skip our button and auto-load the iframe when phone is verified.
  // Use phoneVerifiedAt (not just phoneNumber) so we don't treat unverified or expired verification as verified.
  const hasVerifiedPhone = Boolean(
    currentUser?.phoneVerifiedAt ?? currentUser?.phone_verified_at,
  );

  const { startCheckout, widgetProps, isStarting, isProcessingPayment, reset } =
    useApplePayCheckout();

  const callbacks = {
    onSuccess: () => {
      setToastAlertShown(false);
      refreshTokenBalances();
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        setToastAlert({ type: 'success', message: 'Payment Successful' });
      }
    },
    onAlert: (title, message, type) => {
      setToastAlertShown(false);
      if (type === 'error') {
        setToastAlert({ type: 'error', message: title ?? 'Payment Failed' });
      } else if (type === 'info') {
        setToastAlert({ type: 'info', message: title ?? message });
      }
    },
    onPhoneVerificationExpired: () => {
      dispatch(
        openModal({
          name: 'verifyPhoneNumber',
          props: { onContinue: () => {} },
        }),
      );
    },
    onError: (err) => {
      if (err) toast.error('Failed to create Apple Pay link');
    },
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // On web, only when phone is verified: auto-request the payment link.
  // When not verified we show our custom button and wait for the user to click first.
  useEffect(() => {
    if (!amount || amount < 5) return;
    if (!isWeb || !hasVerifiedPhone) return;
    if (widgetProps || isStarting) return;
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    startCheckout(amount, callbacks, { source: 'deposit' }).catch(() => {});
  }, [amount, isWeb, hasVerifiedPhone, widgetProps, isStarting, startCheckout]);

  useEffect(() => {
    if (!toastAlertShown && toastAlert) {
      if (toastAlert.type === 'success') {
        toast.success(toastAlert.message);
        onClose();
      } else if (toastAlert.type === 'error') {
        dispatch(openModal({ name: 'paymentFailed' }));
      } else if (toastAlert.type === 'info') {
        toast.info(toastAlert.message);
        onClose();
      }
      setToastAlertShown(true);
    }
  }, [toastAlert, toastAlertShown]);

  return (
    <Modal onClose={onClose}>
      <ContentContainer>
        <ParagraphLargeText style={{ textAlign: 'center', width: '100%' }}>
          Deposit
        </ParagraphLargeText>
        <HighCohesionContainer>
          <UsdcIcon width={40} height={40} style={{ marginBottom: 10 }} />
          <Heading3Text
            style={{ textAlign: 'center', width: '100%', fontWeight: 600 }}
          >
            Buy {formatUSD(amount)} of USDC
          </Heading3Text>
          <ParagraphLargeText
            style={{
              textAlign: 'center',
              width: '100%',
              color: theme.text.secondary,
            }}
          >
            USDC price: $1.00
          </ParagraphLargeText>
        </HighCohesionContainer>
        {widgetProps ? (
          <>
            <ApplePayWidget {...widgetProps} />
            {Capacitor.isNativePlatform() && (
              <ApplePayButton loading disabled />
            )}
          </>
        ) : isWeb && hasVerifiedPhone ? (
          <ApplePayButton
            loading={isStarting || isProcessingPayment}
            disabled
          />
        ) : (
          <ApplePayButton
            onClick={() => startCheckout(amount, callbacks, { source: 'deposit' }).catch(() => {})}
            loading={isStarting || isProcessingPayment}
          />
        )}
        <HighCohesionContainer style={{ gap: 10 }}>
          <Line />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <LockIcon width={16} height={16} />
            <ParagraphLargeText
              style={{
                textAlign: 'center',
                width: '100%',
                fontWeight: 500,
                fontSize: 12,
                color: theme.text.secondary,
              }}
            >
              Secured by{' '}
              <span style={{ color: theme.primary.primary }}>Coinbase</span>
            </ParagraphLargeText>
          </div>
        </HighCohesionContainer>
      </ContentContainer>
    </Modal>
  );
};

export default ApplePayModal;
